import type { SampleAllocationItem } from "@unbox/api";
import { SearchLg, Trash01, UploadCloud02 } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { Heading } from "react-aria-components";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { classifyImportRow, type ImportRow, MAX_MONTHLY_CAP, summarizeImportRows } from "./allocations-csv";

const SUBTITLE =
  "Choose how many units of each product creators can request each month. Once requests come in, you'll see them on your Pending Requests.";

function ManualRow({
  item,
  draft,
  autoFocus,
  onChange,
}: {
  item: SampleAllocationItem;
  draft: number;
  autoFocus?: boolean;
  onChange: (value: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!autoFocus) return;
    inputRef.current?.focus();
    inputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [autoFocus]);

  return (
    <tr className="border-t border-secondary">
      <td className="py-2">
        <div className="flex items-center justify-center">
          <input
            ref={inputRef}
            type="number"
            min={0}
            aria-label={`Monthly units for ${item.productName}`}
            value={String(draft)}
            max={MAX_MONTHLY_CAP}
            onChange={(e) => {
              const next = Number(e.target.value);
              onChange(Number.isNaN(next) ? 0 : Math.min(MAX_MONTHLY_CAP, Math.max(0, Math.trunc(next))));
            }}
            className="w-20 rounded-lg border border-secondary bg-primary px-3 py-2 text-center text-sm text-primary outline-none focus:border-brand"
          />
        </div>
      </td>
      <td className="py-2">
        <div className="flex min-w-0 items-center gap-3">
          <MerchantLogo name={item.productName} logoUrl={item.imageUrl} size="sm" />
          <Tooltip title={item.productName}>
            <TooltipTrigger className="min-w-0 max-w-[45ch] truncate">
              <span className="text-sm font-medium text-primary">{item.productName}</span>
            </TooltipTrigger>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

function CsvMergedRow({
  item,
  importRow,
  oldValue,
  willOverwrite,
  onUnitsChange,
}: {
  item: SampleAllocationItem;
  importRow: ImportRow;
  oldValue: number;
  willOverwrite: boolean;
  onUnitsChange: (units: string) => void;
}) {
  return (
    <tr className="border-t border-secondary">
      <td className="py-2">
        <div className="flex items-center justify-center gap-2">
          {willOverwrite && <span className="text-sm text-tertiary line-through">{oldValue}</span>}
          <input
            type="number"
            min={0}
            aria-label={`Monthly units for ${item.productName}`}
            value={importRow.units}
            max={MAX_MONTHLY_CAP}
            onChange={(e) => onUnitsChange(e.target.value)}
            className="w-20 rounded-lg border border-secondary bg-primary px-3 py-2 text-center text-sm text-primary outline-none focus:border-brand"
          />
        </div>
      </td>
      <td className="py-2">
        <div className="flex min-w-0 items-center gap-3">
          <MerchantLogo name={item.productName} logoUrl={item.imageUrl} size="sm" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <Tooltip title={item.productName}>
              <TooltipTrigger className="min-w-0 max-w-[45ch] truncate">
                <span className="text-sm font-medium text-primary">{item.productName}</span>
              </TooltipTrigger>
            </Tooltip>
            {willOverwrite && <span className="text-xs font-medium text-warning-primary">Overwrite</span>}
          </div>
        </div>
      </td>
    </tr>
  );
}

function DimmedRow({ item, value }: { item: SampleAllocationItem; value: number }) {
  return (
    <tr className="border-t border-secondary opacity-40">
      <td className="py-2">
        <div className="flex items-center justify-center">
          <span className="flex w-20 items-center justify-center rounded-lg border border-secondary bg-primary px-3 py-2 text-center text-sm text-tertiary">
            {value}
          </span>
        </div>
      </td>
      <td className="py-2">
        <div className="flex min-w-0 items-center gap-3">
          <MerchantLogo name={item.productName} logoUrl={item.imageUrl} size="sm" />
          <span className="truncate text-sm font-medium text-primary">{item.productName}</span>
        </div>
      </td>
    </tr>
  );
}

function CountDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-sm text-tertiary">
      <span className={cx("size-2 shrink-0 rounded-full", className)} />
      {label}
    </span>
  );
}

export function AllocationsManualStep({
  items,
  drafts,
  importRows,
  fileName,
  capByProductId,
  isPending,
  focusProductId,
  onDraftChange,
  onImportRowChange,
  onImport,
  onRemoveFile,
  onDownloadTemplate,
  onCancel,
  onSave,
}: {
  items: SampleAllocationItem[];
  drafts: Record<string, number>;
  importRows: ImportRow[] | null;
  fileName: string | null;
  capByProductId: Map<string, number>;
  isPending: boolean;
  focusProductId?: string;
  onDraftChange: (productId: string, value: number) => void;
  onImportRowChange: (key: string, units: string) => void;
  onImport: () => void;
  onRemoveFile: () => void;
  onDownloadTemplate: () => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const [search, setSearch] = useState("");

  const sorted = useMemo(
    () => [...items].sort((a, b) => (b.monthlyCap > 0 ? 1 : 0) - (a.monthlyCap > 0 ? 1 : 0)),
    [items],
  );

  const importedByProductId = useMemo(() => {
    if (!importRows) return null;
    const map = new Map<string, { row: ImportRow; willOverwrite: boolean }>();
    for (const row of importRows) {
      if (!row.productId) continue;
      const status = classifyImportRow(row, capByProductId);
      if (status.status === "valid") {
        map.set(row.productId, { row, willOverwrite: status.willOverwrite });
      }
    }
    return map;
  }, [importRows, capByProductId]);

  const summary = useMemo(
    () => (importRows ? summarizeImportRows(importRows, capByProductId) : null),
    [importRows, capByProductId],
  );

  const visibleItems = useMemo(() => {
    const filtered = sorted.filter((i) => i.productName.toLowerCase().includes(search.trim().toLowerCase()));
    if (!importedByProductId) return filtered;
    return [...filtered].sort((a, b) => {
      const aIn = importedByProductId.has(a.productId) ? 1 : 0;
      const bIn = importedByProductId.has(b.productId) ? 1 : 0;
      return bIn - aIn;
    });
  }, [sorted, search, importedByProductId]);

  const hasManualChanges = useMemo(
    () => items.some((i) => (drafts[i.productId] ?? i.monthlyCap) !== i.monthlyCap),
    [items, drafts],
  );

  const canSave = importRows ? (summary?.valid ?? 0) > 0 : hasManualChanges;

  return (
    <>
      <div className="flex flex-col gap-1 px-6 pt-6 pb-4">
        <Heading slot="title" className="text-lg font-semibold text-primary pb-3">
          Set Allocations
        </Heading>
        <p className="max-w-[60ch] text-sm text-tertiary">{SUBTITLE}</p>
      </div>

      <div className="flex items-center justify-between gap-3 px-6 pt-2 pb-6">
        <div className="flex w-72 items-center gap-2 rounded-lg border border-secondary px-3 py-2">
          <SearchLg className="size-4 text-tertiary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${items.length} Total Products`}
            className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
          />
        </div>
        <Button color="primary" size="sm" iconLeading={UploadCloud02} onClick={onImport}>
          Import CSV
        </Button>
      </div>

      <div className="flex flex-col gap-2.5 px-6 pb-3">
        <p className="text-sm text-tertiary">
          Export your current allocations to edit in bulk,{" "}
          <button
            type="button"
            onClick={onDownloadTemplate}
            className="font-semibold text-brand-secondary underline hover:opacity-80"
          >
            Download Template
          </button>
          .
        </p>

        {fileName && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-lg border border-secondary px-3 pt-1.5 pb-3 w-full">
              <img src="/csv_icon.png" alt="CSV" className="size-[30px] shrink-0" />
              <span className="w-full max-w-[100ch] truncate text-sm font-medium text-primary">{fileName}</span>
              <button
                type="button"
                aria-label="Remove uploaded file"
                onClick={onRemoveFile}
                className="text-tertiary hover:text-primary"
              >
                <Trash01 className="size-4" />
              </button>
            </div>
            {summary && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <CountDot className="bg-utility-success-500" label={`${summary.valid} Products Imported`} />
                {summary.willOverwrite > 0 && (
                  <>
                    <span className="text-quaternary">·</span>
                    <CountDot className="bg-utility-warning-500" label={`${summary.willOverwrite} Overwrite`} />
                  </>
                )}
                {summary.error > 0 && (
                  <>
                    <span className="text-quaternary">·</span>
                    <CountDot
                      className="bg-utility-error-500"
                      label={`${summary.error} Unrecognized ${summary.error === 1 ? "product" : "products"}`}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto border-t border-secondary">
        <table className="w-full">
          <thead className="sticky top-0 bg-secondary">
            <tr>
              <th className="w-[25%] py-2 text-center text-xs font-semibold text-quaternary">Monthly Allocation</th>
              <th className="py-2 text-left text-xs font-semibold text-quaternary">Product SKU</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item) => {
              if (importedByProductId) {
                const imported = importedByProductId.get(item.productId);
                if (imported) {
                  return (
                    <CsvMergedRow
                      key={item.productId}
                      item={item}
                      importRow={imported.row}
                      oldValue={capByProductId.get(item.productId) ?? 0}
                      willOverwrite={imported.willOverwrite}
                      onUnitsChange={(units) => onImportRowChange(imported.row.key, units)}
                    />
                  );
                }
                return <DimmedRow key={item.productId} item={item} value={drafts[item.productId] ?? item.monthlyCap} />;
              }
              return (
                <ManualRow
                  key={item.productId}
                  item={item}
                  draft={drafts[item.productId] ?? item.monthlyCap}
                  autoFocus={focusProductId === item.productId}
                  onChange={(value) => onDraftChange(item.productId, value)}
                />
              );
            })}
            {visibleItems.length === 0 && (
              <tr>
                <td colSpan={2} className="px-5 py-8 text-center text-sm text-tertiary">
                  No products found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-3 border-t border-secondary px-6 py-4">
        <Button color="secondary" size="md" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" size="md" isLoading={isPending} isDisabled={!canSave} onClick={onSave}>
          {importRows ? "Apply Changes" : "Save"}
        </Button>
      </div>
    </>
  );
}
